import { ErrorHandler, Injectable } from '@angular/core';

/** Custom global error handler that detects webpack chunk-load failures
 *  and self-heals by reloading the page.
 *
 *  Background: the Angular app is split into hash-named chunks (e.g.
 *  `desktop.def456.js`). Every server deploy generates new hashes. If a
 *  client (especially the Electron desktop app, whose Chromium cache
 *  survives reinstalls under the user profile) has a stale main bundle
 *  cached from a previous session, the chunk manifest baked into that
 *  bundle still points at chunk filenames that no longer exist on the
 *  server. The next lazy-load attempt (typically the first route nav
 *  after a fresh launch) tries to call into an `undefined` module
 *  factory and throws — leaving the user staring at a grey screen with
 *  only a console error to explain it.
 *
 *  Reloading the page forces the browser to re-fetch the HTML and the
 *  main bundle, which then carries the up-to-date chunk manifest, and
 *  the lazy-load succeeds. A cooldown via sessionStorage prevents an
 *  infinite reload loop if the issue is something else (e.g. the
 *  server is genuinely broken).
 */
@Injectable()
export class ChunkLoadErrorHandler extends ErrorHandler {
    /** sessionStorage key holding the timestamp of the last auto-reload.
     *  Survives the reload itself (sessionStorage persists across same-
     *  origin reloads in the same tab) but resets on a fresh tab / window. */
    private static readonly RELOAD_FLAG_KEY = 'visarc-chunk-load-reload-at';

    /** Only allow one auto-reload per cooldown window. If the page reloads
     *  and immediately throws the same chunk error, falling through to the
     *  default handler is better than an endless reload loop. */
    private static readonly RELOAD_COOLDOWN_MS = 30_000;

    override handleError(error: unknown): void {
        if (this.isChunkLoadError(error) && this.canReload()) {
            try {
                sessionStorage.setItem(
                    ChunkLoadErrorHandler.RELOAD_FLAG_KEY,
                    String(Date.now()),
                );
            } catch {
                // sessionStorage can throw in private browsing / disabled
                // storage configurations — proceed with the reload anyway.
                // Worst case is we lose the loop guard for this session.
            }
            // eslint-disable-next-line no-console
            console.warn('[ChunkLoadErrorHandler] auto-reloading to recover from stale chunk', error);
            window.location.reload();
            return;
        }
        super.handleError(error);
    }

    /** Identifies the error fingerprints associated with a chunk-load
     *  failure across the webpack versions Angular has shipped with. */
    private isChunkLoadError(error: unknown): boolean {
        if (!error || typeof error !== 'object') return false;

        // Unhandled promise rejections are wrapped in `{ rejection: <real error> }`
        // by Angular's zone integration. Look in both places so the matchers
        // hit either form.
        const e = error as { message?: unknown; name?: unknown; stack?: unknown; rejection?: unknown };
        const message = String(e.message ?? (e.rejection as any)?.message ?? '');
        const name = String(e.name ?? (e.rejection as any)?.name ?? '');
        const stack = String(e.stack ?? (e.rejection as any)?.stack ?? '');

        // Modern webpack: dedicated error class.
        if (name === 'ChunkLoadError') return true;
        // Webpack's classic chunk-load failure message.
        if (/Loading chunk \S+ failed/i.test(message)) return true;
        // ES-modules-flavoured dynamic-import failure.
        if (/Failed to fetch dynamically imported module/i.test(message)) return true;
        // The specific symptom we see in the Visarc Intranet after a stale-
        // cache deploy: webpack's manifest returns `undefined` for a chunk
        // that no longer exists, then tries to `.call()` it. Webpack itself
        // throws here, so the TOP of the stack is `__webpack_require__` —
        // not the message + "webpack somewhere in the stack" combo, which
        // would also match unrelated `.call()` TypeErrors (any app code
        // running inside a webpack bundle has webpack frames deep in its
        // stack). Look only at the first couple of frames AND require the
        // frame text to start with `at __webpack_require__ (bootstrap:` —
        // that's the unique signature of webpack's own module loader
        // throwing, vs application code higher up the stack throwing on
        // something webpack-loaded.
        const isCallOfUndefined =
            /Cannot read propert(?:y 'call' of undefined|ies of undefined \(reading 'call'\))/i.test(message);
        if (isCallOfUndefined) {
            // First line is the error message itself; frames start at line 2.
            const topFrames = stack.split('\n').slice(1, 3).join('\n');
            if (/\s*at\s+(?:Function\.)?__webpack_require__\s*\(bootstrap:/i.test(topFrames)) {
                return true;
            }
        }

        return false;
    }

    /** Returns true unless we've already auto-reloaded within the cooldown
     *  window. Prevents an infinite reload loop when the underlying issue
     *  isn't actually a stale-cache problem. */
    private canReload(): boolean {
        try {
            const raw = sessionStorage.getItem(ChunkLoadErrorHandler.RELOAD_FLAG_KEY);
            if (!raw) return true;
            const last = Number(raw);
            if (!Number.isFinite(last)) return true;
            return Date.now() - last > ChunkLoadErrorHandler.RELOAD_COOLDOWN_MS;
        } catch {
            return true;
        }
    }
}
